import { type ReactNode } from 'react'

import styles from './ConfirmDialog.module.css'
import { Modal } from './Modal'

interface ConfirmDialogProps {
  title: string
  /** What happens if the user confirms (the consequence, not "are you sure?"). */
  children: ReactNode
  confirmLabel: string
  /** `danger` for actions that take something away (remove, unpin). */
  tone?: 'primary' | 'danger'
  busy?: boolean
  /** Shown above the buttons when the action failed. */
  error?: string | null
  onConfirm: () => void
  onCancel: () => void
}

/** A small confirmation dialog that states the consequence of an action before it is taken. */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  tone = 'primary',
  busy = false,
  error = null,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Modal title={title} onClose={onCancel} size="sm">
      <div className={styles.body}>{children}</div>
      {error ? (
        <div className={styles.error} role="alert">
          {error}
        </div>
      ) : null}
      <div className={styles.actions}>
        <button type="button" className={styles.secondary} onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className={tone === 'danger' ? styles.danger : styles.primary}
          onClick={onConfirm}
          disabled={busy}
          data-autofocus
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </Modal>
  )
}
